import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './MyAdsWithContent.css';

// Mock data for active ads
const mockAds = [
  {
    id: '1',
    type: 'sell' as const,
    price: 1450.00,
    currency: 'USDT',
    available: 2,
    limitMin: 1440.00,
    limitMax: 2900.00,
    limitCurrency: 'NGN',
    timeLimit: 'min'
  },
  {
    id: '2',
    type: 'buy' as const,
    price: 1440.00,
    currency: 'USDT',
    available: 5,
    limitMin: 1430.00,
    limitMax: 7200.00,
    limitCurrency: 'NGN',
    timeLimit: 'min'
  }
];

interface Ad {
  id: string;
  type: 'buy' | 'sell';
  price: number;
  currency: string;
  available: number;
  limitMin: number;
  limitMax: number;
  limitCurrency: string;
  timeLimit: string;
}

interface DeleteOfferModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  ad: Ad | null;
}

const DeleteOfferModal: React.FC<DeleteOfferModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  ad
}) => {
  if (!isOpen || !ad) return null;

  return (
    <div className="delete-modal-overlay" onClick={onClose}>
      <div className="delete-modal-content" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="delete-modal-header">
          <h2 className="delete-modal-title">Delete Offer</h2>
          <button className="delete-modal-close" onClick={onClose} aria-label="Close">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>

        {/* Subtitle */}
        <p className="delete-modal-subtitle">This action cannot be undone</p>

        {/* Preview Card */}
        <div className="delete-modal-preview">
          <div className="delete-modal-preview-header">
            <div className="delete-modal-preview-price-section">
              <span className="delete-modal-preview-price">{ad.price.toFixed(2)}</span>
              <span className="delete-modal-preview-currency">/{ad.currency}</span>
            </div>
            <div className="delete-modal-preview-available-section">
              <span className="delete-modal-preview-available-label">Available</span>
              <span className="delete-modal-preview-available-value">{ad.available} {ad.currency}</span>
            </div>
          </div>
          <div className="delete-modal-preview-limit">
            Limit {ad.limitMin.toFixed(2)} - {ad.limitMax.toFixed(2)}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="delete-modal-actions">
          <button className="delete-modal-btn delete-modal-btn-cancel" onClick={onClose}>
            Cancel
          </button>
          <button className="delete-modal-btn delete-modal-btn-delete" onClick={onConfirm}>
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};

interface OfferDeletedModalProps {
  isOpen: boolean;
  onClose: () => void;
  adId: string | null;
}

const OfferDeletedModal: React.FC<OfferDeletedModalProps> = ({
  isOpen,
  onClose,
  adId
}) => {
  if (!isOpen || !adId) return null;

  // Generate a mock transaction ID based on ad ID
  const transactionId = `${adId}8819909-b2a`;

  return (
    <div className="success-modal-overlay" onClick={onClose}>
      <div className="success-modal-content" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="success-modal-header">
          <h2 className="success-modal-title">Offer Deleted</h2>
          <button className="success-modal-close" onClick={onClose} aria-label="Close">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>

        {/* Success Icon */}
        <div className="success-modal-icon-wrapper">
          <div className="success-modal-icon-glow"></div>
          <div className="success-modal-icon">
            <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
              <circle cx="32" cy="32" r="28" stroke="#22c55e" strokeWidth="4"/>
              <path d="M20 32L28 40L44 24" stroke="#22c55e" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
        </div>

        {/* Success Message */}
        <p className="success-modal-message">
          Offer <span className="success-modal-message-id">{transactionId}...</span> has been successfully closed.
        </p>
      </div>
    </div>
  );
};

const MyAdsWithContent: React.FC = () => {
  const navigate = useNavigate();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [adToDelete, setAdToDelete] = useState<Ad | null>(null);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [deletedAdId, setDeletedAdId] = useState<string | null>(null);

  const handleBack = () => {
    navigate(-1);
  };

  const handleHistory = () => {
    console.log('Navigate to ads history');
  };

  const handlePost = () => {
    navigate('/post-ad-step-1');
  };

  const handleDeleteOffer = (ad: Ad) => {
    setAdToDelete(ad);
    setIsDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setAdToDelete(null);
  };

  const handleConfirmDelete = () => {
    if (adToDelete) {
      console.log('Deleting offer:', adToDelete.id);
      // Store the ID before clearing
      setDeletedAdId(adToDelete.id);
      // Close delete modal
      setIsDeleteModalOpen(false);
      setAdToDelete(null);
      // Show success modal
      setIsSuccessModalOpen(true);
    }
  };

  const handleCloseSuccessModal = () => {
    setIsSuccessModalOpen(false);
    setDeletedAdId(null);
  };

  const renderAdCard = (ad: Ad) => {
    const isSell = ad.type === 'sell';

    return (
      <div key={ad.id} className="my-ads-card">
        {/* Price Row */}
        <div className="my-ads-card-header">
          <div className="my-ads-card-price-section">
            <span className="my-ads-card-price">{ad.price.toFixed(2)}</span>
            <span className="my-ads-card-currency">/{ad.currency}</span>
          </div>
          <div className="my-ads-card-available-section">
            <span className="my-ads-card-available-label">Available</span>
            <span className="my-ads-card-available-value">{ad.available} {ad.currency}</span>
          </div>
        </div>

        {/* Type Badge and Time Row */}
        <div className="my-ads-card-meta">
          <span className={`my-ads-card-type-badge ${isSell ? 'sell' : 'buy'}`}>
            {isSell ? 'Sell' : 'Buy'}
          </span>
          <span className="my-ads-card-time">Time: {ad.timeLimit}</span>
        </div>

        {/* Limit Info */}
        <div className="my-ads-card-limit">
          Limit {ad.limitMin.toFixed(2)} - {ad.limitMax.toFixed(2)} {ad.limitCurrency}
        </div>

        {/* Delete Button */}
        <button
          className={`my-ads-card-delete-btn ${isSell ? 'sell' : 'buy'}`}
          onClick={() => handleDeleteOffer(ad)}
        >
          Delete Offer
        </button>
      </div>
    );
  };

  return (
    <div className="my-ads-container">
      {/* Header */}
      <header className="my-ads-header">
        <button className="my-ads-back-button" onClick={handleBack} aria-label="Go back">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>

        <h1 className="my-ads-title">My Ads</h1>

        <div className="my-ads-header-actions">
          <button className="my-ads-history-button" onClick={handleHistory}>
            <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
              <path d="M5.5 1V5.5L8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <circle cx="5.5" cy="5.5" r="4.5" stroke="currentColor" strokeWidth="1.5"/>
            </svg>
            History
          </button>

          <button className="my-ads-post-button" onClick={handlePost}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M8 3V13M3 8H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Post
          </button>
        </div>
      </header>

      {/* Ads List Content */}
      <div className="my-ads-list-content">
        {mockAds.map(renderAdCard)}
      </div>

      {/* Footer Pagination */}
      <footer className="my-ads-footer">
        <div className="my-ads-pagination">
          <button className="my-ads-pagination-button" disabled>
            Previous
          </button>
          <span className="my-ads-pagination-text">Page 1 of 1</span>
          <button className="my-ads-pagination-button" disabled>
            Next
          </button>
        </div>
      </footer>

      {/* Delete Offer Modal */}
      <DeleteOfferModal
        isOpen={isDeleteModalOpen}
        onClose={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
        ad={adToDelete}
      />

      {/* Offer Deleted Success Modal */}
      <OfferDeletedModal
        isOpen={isSuccessModalOpen}
        onClose={handleCloseSuccessModal}
        adId={deletedAdId}
      />
    </div>
  );
};

export default MyAdsWithContent;
